import { Component, OnInit, signal } from '@angular/core';
import { Router, Route } from '@angular/router';
import { MatSidenavModule } from '@angular/material/sidenav';
import { RouterOutlet } from '@angular/router';
import { NavigationItem } from '../models/navigation-item.model';
import { ToolbarComponent } from '../shared/components/toolbar/toolbar.component';
import { MenuDrawerComponent } from '../shared/components/menu-drawer/menu-drawer.component';
import { CustomFooter2Component } from '../shared/components/custom-footer2/custom-footer2.component';
import { PermissionService } from '../services/permission/permission.service';

@Component({
  selector: 'app-pages',
  standalone: true,
  imports: [
    MatSidenavModule,
    RouterOutlet,
    ToolbarComponent,
    MenuDrawerComponent,
    CustomFooter2Component,
  ],
  templateUrl: './pages.component.html',
  styleUrls: ['./pages.component.scss'],
})
export class PagesComponent implements OnInit {
  routeData = signal<NavigationItem[]>([]);

  private baseIgnoredRoutes: string[] = ['login', '**', 'forgot-password'];

  constructor(
    private router: Router,
    private permissionService: PermissionService,
  ) {}

  ngOnInit(): void {
    this.buildNavigation();
  }

  private async buildNavigation(): Promise<void> {
    const allRoutes = this.router.config;
    const navigationData = await this.extractRoutes(allRoutes);
    const normalizedData = this.normalizeRoutes(navigationData);

    let filteredRouteData = normalizedData.filter(
      (r) => r.path !== '' && !r.metaData?.isSystemPage,
    );

    // Permission checking routes
    filteredRouteData = filteredRouteData.filter((r: NavigationItem) => {
      if (r.children?.length) {
        r.children = r.children.filter((childRoute: NavigationItem) =>
          !childRoute.metaData?.isSystemPage &&
          (childRoute.permission
            ? this.permissionService.hasPermission(childRoute.permission)
            : false),
        );
        return r.children.length > 0;
      }

      if (r.permission) {
        return this.permissionService.hasPermission(r.permission);
      } else {
        return false;
      }
    });

    this.routeData.set(filteredRouteData);
  }

  private async extractRoutes(routes: Route[]): Promise<NavigationItem[]> {
    const routePromises = routes.map(async (route: Route) => {
      if (route.loadChildren && typeof route.loadChildren === 'function') {
        try {
          const childRoutes = await (route.loadChildren as () => Promise<Route[]>)();
          const childRouteData = await this.extractRoutes(childRoutes);

          if (!this.baseIgnoredRoutes.includes(route.path!)) {
            return {
              permission: route?.data?.['permission'] || null,
              metaData: route?.data?.['meta'] || { navName: '', navIcon: '' },
              children: childRouteData,
              path: route.path!,
            };
          }
        } catch (e) {
          console.log('error loading child, ' + e);
          return null;
        }
      } else if (!this.baseIgnoredRoutes.includes(route.path!)) {
        return {
          permission: route?.data?.['permission'] || null,
          metaData: route?.data?.['meta'] || { navName: '', navIcon: '' },
          children: [],
          path: route.path!,
        };
      }
      return null;
    });

    const resolvedPaths = await Promise.all(routePromises);
    return resolvedPaths.filter((r) => r !== null) as NavigationItem[];
  }

  private normalizeRoutes(routes: NavigationItem[]): NavigationItem[] {
    const result: NavigationItem[] = [];
    routes.forEach((route) => {
      if (route.path === '') {
        route?.children?.forEach((child) => {
          result.push(this.cleanEmptyPaths(child));
        });
      } else {
        result.push(this.cleanEmptyPaths(route));
      }
    });
    return result;
  }

  private cleanEmptyPaths(route: NavigationItem): NavigationItem {
    if (route.children && route.children.length > 0) {
      route.children = route.children
        .filter((child) => child.path !== '')
        .map((child) => this.cleanEmptyPaths(child));
    }
    return route;
  }
}
